import { Filter, LayoutGrid, Users, Workflow } from "lucide-react";
import { DsAlert, DsButton, DsIcon, DsInput, DsLabel } from "../../../design-system";

function ChoiceCard({ selected, disabled, onClick, icon, title, children }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onClick}
      className={`flex w-full flex-col rounded-xl border p-3.5 text-left transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))] disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none ${
        selected
          ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.08)] ring-1 ring-[hsl(var(--ds-primary))]"
          : "border-[hsl(var(--ds-border))] hover:border-[hsl(var(--ds-primary)/0.45)]"
      }`}
    >
      <span className="mb-1.5 flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-primary))]">
          <DsIcon icon={icon} size="sm" />
        </span>
        <span className="text-sm font-semibold text-[hsl(var(--ds-fg))]">{title}</span>
      </span>
      <span className="text-xs leading-snug text-[hsl(var(--ds-muted-fg))]">{children}</span>
    </button>
  );
}

export default function RoleInfoStep({
  draft,
  patchDraft,
  isSystem,
  isEdit,
  originalType,
  error,
  filterCount,
  activeEmployeeCount,
  accessTypeBlocked,
  isDelegate,
  onChooseAll,
  onChooseFilter,
  onOpenFilters,
}) {
  const switchingToAction = isEdit && originalType === "access_based" && draft.roleType === "action_based";

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6">
      {error ? <DsAlert tone="danger">{error}</DsAlert> : null}

      {isSystem ? (
        <DsAlert tone="info">
          This is a default system role. Its name, type and assignment are fixed; you can change only
          the modules, permissions and data access it grants.
        </DsAlert>
      ) : null}

      <section className="space-y-4">
        <div>
          <DsLabel htmlFor="urm-role-name">Role name *</DsLabel>
          <DsInput
            id="urm-role-name"
            value={draft.name}
            disabled={isSystem}
            maxLength={100}
            autoFocus={!isEdit}
            placeholder="e.g. Payroll Executive"
            onChange={(e) => patchDraft({ name: e.target.value })}
          />
          {isEdit && !isSystem ? (
            <p className="mt-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">
              Renaming changes only the display name. The role code stays the same, so assigned
              employees keep this role.
            </p>
          ) : null}
        </div>
        <div>
          <DsLabel htmlFor="urm-role-desc">Description</DsLabel>
          <textarea
            id="urm-role-desc"
            rows={2}
            maxLength={500}
            disabled={isSystem}
            value={draft.description}
            placeholder="What this role is for, e.g. Runs monthly payroll and statutory filings"
            onChange={(e) => patchDraft({ description: e.target.value })}
            className="w-full rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 py-2 text-sm text-[hsl(var(--ds-fg))] outline-none placeholder:text-[hsl(var(--ds-muted-fg))] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))] disabled:opacity-60"
          />
        </div>
      </section>

      {!isSystem ? (
        <section aria-labelledby="urm-type-h" className="space-y-2">
          <h4 id="urm-type-h" className="text-sm font-semibold text-[hsl(var(--ds-fg))]">
            Role type *
          </h4>
          <div role="radiogroup" aria-labelledby="urm-type-h" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <ChoiceCard
              icon={LayoutGrid}
              title="Access Based"
              selected={draft.roleType === "access_based"}
              disabled={accessTypeBlocked}
              onClick={() => patchDraft({ roleType: "access_based" })}
            >
              Gives employees access to admin screens (Payroll, Attendance…) and decides whose data
              they can see. Next steps: modules, permissions and data access.
            </ChoiceCard>
            <ChoiceCard
              icon={Workflow}
              title="Action Based"
              selected={draft.roleType === "action_based"}
              onClick={() => patchDraft({ roleType: "action_based" })}
            >
              Workflow assignee only: Helpdesk tickets, policy checklists and approvals. No screen or
              data access. Goes straight to Review.
            </ChoiceCard>
          </div>
          {accessTypeBlocked ? (
            <p className="text-[11px] text-[hsl(var(--ds-muted-fg))]">
              You don&apos;t have permission to set screen or data access, so you can create only Action
              Based roles.
            </p>
          ) : null}
          {switchingToAction ? (
            <DsAlert tone="warning">
              Saving as Action Based removes this role&apos;s screen and data access. Employees with this
              role lose that access.
            </DsAlert>
          ) : null}
        </section>
      ) : null}

      {!isSystem ? (
        <section aria-labelledby="urm-status-h" className="space-y-2">
          <h4 id="urm-status-h" className="text-sm font-semibold text-[hsl(var(--ds-fg))]">
            Status
          </h4>
          <div role="radiogroup" aria-labelledby="urm-status-h" className="inline-flex rounded-lg border border-[hsl(var(--ds-border))] p-0.5">
            {[
              { v: true, label: "Active" },
              { v: false, label: "Inactive" },
            ].map((opt) => (
              <button
                key={opt.label}
                type="button"
                role="radio"
                aria-checked={draft.active === opt.v}
                onClick={() => patchDraft({ active: opt.v })}
                className={`rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))] ${
                  draft.active === opt.v
                    ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                    : "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-[hsl(var(--ds-muted-fg))]">
            An inactive role keeps its settings, but employees on it get only default employee access
            until it is reactivated.
          </p>
        </section>
      ) : null}

      {!isSystem ? (
        <section aria-labelledby="urm-assign-h" className="space-y-2">
          <h4 id="urm-assign-h" className="text-sm font-semibold text-[hsl(var(--ds-fg))]">
            Who gets this role? *
          </h4>
          <p className="text-[11px] text-[hsl(var(--ds-muted-fg))]">
            An employee holds one custom role at a time. Assigning this role replaces any other custom
            role they have{isDelegate ? ". You are never assigned or unassigned by your own changes" : ""}.
          </p>
          <div role="radiogroup" aria-labelledby="urm-assign-h" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <ChoiceCard icon={Users} title="All employees" selected={draft.assignmentType === "all"} onClick={onChooseAll}>
              Every active employee
              {Number.isFinite(activeEmployeeCount) ? ` (${activeEmployeeCount})` : ""}. You&apos;ll be
              asked to confirm.
            </ChoiceCard>
            <ChoiceCard
              icon={Filter}
              title="Selected employees"
              selected={draft.assignmentType === "filter"}
              onClick={onChooseFilter}
            >
              Pick employees with filters (department, branch, designation…).
            </ChoiceCard>
          </div>
          {draft.assignmentType === "filter" ? (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.5)] px-3 py-2">
              <span className="text-sm text-[hsl(var(--ds-fg))]">
                {filterCount > 0
                  ? `${filterCount} employee${filterCount === 1 ? "" : "s"} selected`
                  : "No employees selected yet"}
              </span>
              <DsButton size="sm" variant="outline" onClick={onOpenFilters}>
                <DsIcon icon={Filter} size="sm" />
                {filterCount > 0 ? "Change selection" : "Select employees"}
              </DsButton>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
